import React, { useEffect, useState } from 'react';
import cn from "classnames";
import { partial } from "lodash";
import makeStyles from "@mui/styles/makeStyles";
import { Grid, Typography } from '@mui/material';

import ChipList from '@dyl/components/ChipList';
import RubricBlock from '@dyl/blocks/RubricBlock';
import SimpleTaskField from '@dyl/blocks/SimpleTaskField';
import useTaskField from '@dyl/pages/Tasks/edit/useTaskField';
import SectionDividers from "@dyl/components/SectionDividers";
import useLocalTaskError from '@dyl/pages/Tasks/edit/useLocalTaskError';
import TaskRadioOptionBlock from "@dyl/blocks/TaskRadioOptionBlock";
import ImagePicker from '@dyl/components/ImagePicker';
import TaskRadioImagesBlock from "@dyl/blocks/TaskRadioImagesBlock";
import { limitWordsAndSpaces } from '../../../utils/limitWordsAndSpaces';

const useStyles = makeStyles(theme => ({
  root: { maxWidth: theme.breakpoints.values.md },
  partHeader: {
    marginBottom: 32,
    textAlign: 'center',
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '36px',
    lineHeight: '100%',
    letterSpacing: '0',
  },
  groupHeader: {
    marginBottom: 12,
    textAlign: 'left',
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 500,
    fontStyle: 'normal',
    fontSize: '24px',
    lineHeight: '100%',
    letterSpacing: '0',
  },
  groupGrid: { marginBottom: 32 },
  narrativeNote: { marginBottom: theme.spacing(1), color: '#757575' },
  errorText: { color: theme.palette.error.main, marginTop: theme.spacing(1) },
  narrativeLabel: {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: '20px',
    lineHeight: '100%',
    letterSpacing: '0',
    marginBottom: 4,
  },
  body1: {
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: theme.palette.text.primary,
    marginBottom: 8,
  },
}));

function countWords(str = '') {
  return String(str).trim().split(/\s+/).filter(Boolean).length;
}

export default function TaskItems({
  className: propsClass,
  ...other
}) {
  const classes = useStyles();

  const [items] = useTaskField('items');
  const [backgroundImageId, setBackgroundImageId] = useTaskField('background_image');
  const [backgroundImageId2, setBackgroundImageId2] = useTaskField('background_image_2');

  // Example item (usually index 0)
  const exampleIndex = 0;
  // Group 1: Item 1 (index 1)
  const group1Index = 1;
  // Group 2: Item 2 and Item 3 (indexes 2 and 3)
  const group2Indexes = [2, 3];
  // Group 3: Item 4 and Item 5 (indexes 4 and 5)
  const group3Indexes = [4, 5];

  // Find all Part 2 items (phase === 'PHASE_2')
  const part2Indexes = items
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => item.phase === 'PHASE_2')
    .map(({ i }) => i);

  // Find all Part 3 items (phase === 'PHASE_3')
  const part3Indexes = items
    .map((item, i) => ({ item, i }))
    .filter(({ item }) => item.phase === 'PHASE_3')
    .map(({ i }) => i);

  return (
    <div className={cn(classes.root, propsClass)} {...other}>
      <SectionDividers>
        {/* Group 1 */}
        <Grid>
          <Typography variant='h4' className={classes.partHeader}>
            Part 1
          </Typography>
          <Grid container spacing={2} style={{ marginBottom: 16 }}>
            <Grid item xs={12} sm={6}>
            <Typography
  sx={{
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#000000 · 87%',
    marginBottom: 1,
  }}
>
  Background Image
</Typography>
              <ImagePicker
                label="Background image (required)"
                value={backgroundImageId || ''}
                onChange={id => setBackgroundImageId(id)}
                fullWidth
              />
            </Grid>
          </Grid>
<Grid style={{ marginTop: 32, marginBottom: 32 }}>
  <Group index={group1Index} forceGroupNumber={1} />
</Grid>         {/* Example inside Group 1 */}
          {items[exampleIndex] && (
            <ItemPhase1 index={exampleIndex} />
          )}
          {/* Item 1 inside Group 1 */}
          {items[group1Index] && (
            <ItemPhase1 index={group1Index} />
          )}
        </Grid>

        {/* Group 2 */}
        <Grid>
          {/* Render narrative/decorative image from Item 2, force header to "Group 2" */}
          {items[group2Indexes[0]] && (
            <Group index={group2Indexes[0]} forceGroupNumber={2} />
          )}
          {/* Render Item 2 and Item 3 */}
          {group2Indexes.map(idx =>
            items[idx] ? <ItemPhase1 key={idx} index={idx} /> : null
          )}
        </Grid>

        {/* Group 3 */}
        <Grid>
          {/* Render narrative/decorative image from Item 4, force header to "Group 3" */}
          {items[group3Indexes[0]] && (
            <Group index={group3Indexes[0]} forceGroupNumber={3} />
          )}
          {/* Render Item 4 and Item 5 */}
          {group3Indexes.map(idx =>
            items[idx] ? <ItemPhase1 key={idx} index={idx} /> : null
          )}
        </Grid>

        {/* Part 2 */}
        {part2Indexes.length > 0 && (
          <Grid>
            <Typography variant='h4' className={classes.partHeader}>
              Part 2
            </Typography>
            {/* Background Image picker for Part 2 */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              <Grid item xs={12} sm={6}>
               <Typography
  sx={{
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#000000 · 87%',
    marginBottom: 1,
  }}
>
  Background Image
</Typography>
                <ImagePicker
                  label="Background image (required)"
                  value={backgroundImageId2 || ''}
                  onChange={id => setBackgroundImageId2(id)}
                  fullWidth
                />
              </Grid>
            </Grid>
            {/* Render each Part 2 item */}
            {part2Indexes.map(idx =>
              items[idx] ? <ItemPhase2 key={idx} index={idx} /> : null
            )}
          </Grid>
        )}

        {/* Part 3 */}
        {part3Indexes.length > 0 && (
          <Grid>
            <Typography variant='h4' className={classes.partHeader}>
              Part 3
            </Typography>
            {/* Render each Part 3 item */}
            {part3Indexes.map(idx =>
              items[idx] ? <ItemPhase3 key={idx} index={idx} /> : null
            )}
          </Grid>
        )}
      </SectionDividers>
    </div>
  );
}

function Group({ index, forceGroupNumber }) {
  const classes = useStyles();
  const useField = partial(useTaskField, 'items', index);
  const [item, setItem] = useField();
  const group = forceGroupNumber ?? item?.group;

  const narrativeValue = item?.stimulus_text || '';
  const words = countWords(narrativeValue);

  const [shouldShowNarrativeError, setShowNarrativeError] = useState(false);
  const narrativeError = (
    ((!narrativeValue || words < 40) && 'Narrative must be at least 40 words.')
    || (words > 75 && 'Narrative must be at most 75 words.')
    || ''
  );

  useLocalTaskError(narrativeError, () => setShowNarrativeError(true));
  useEffect(() => {
    if (!narrativeError) setShowNarrativeError(false);
  }, [narrativeError]);

  const setDescription = (val) => setItem({ ...item, stimulus_text: val });

  const decorativeImageId = item?.decorative_image || '';

  return (
    <>
      <Typography variant='h5' className={classes.groupHeader}>
        {`Group ${group}`}
      </Typography>

      <Grid container spacing={2} className={classes.groupGrid}>
        <Grid item xs={12}>
          <Typography
  className={classes.narrativeLabel}
  sx={{ marginTop: '20px' }}
>
  Narrative
</Typography>
         <Typography
  sx={{
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: 'text.primary',
    marginBottom: 1,
  }}
>
  The text should contain between 40 and 75 words.
</Typography>
          <SimpleTaskField
            path={['items', index, 'stimulus_text']}
            label='Narrative'
            value={narrativeValue}
            onChange={e => {
              setDescription(limitWordsAndSpaces(e.target.value, 75));
            }}
            multiline
            rows={9}
            style={{ width: "417px", height: "260px" }}
            helperText={(shouldShowNarrativeError && narrativeError) || `${words}/75 words`}
            error={shouldShowNarrativeError && !!narrativeError}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
        <Typography
  sx={{
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '1rem',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#000000 · 87%',
    marginBottom: 1,
  }}
>
  Decorative Image
</Typography>
          <ImagePicker
            label="Decorative image"
            value={decorativeImageId}
            onChange={id => setItem({ ...item, decorative_image: id })}
          />
          <Typography sx={{ fontSize: 13, color: '#757575', marginTop: 1 }}>
            This appears at the bottom of the page. Image size: 110px x 110px.
          </Typography>
        </Grid>
      </Grid>
    </>
  );
}

function ItemPhase1({ index }) {
  const useField = partial(useTaskField, 'items', index);
  const [item] = useField();

  const [answers, setAnswers, answersError] = useTaskField('items', index, 'answers', 0, 'key');

  // Use separate fields for left and right questions
  const [qLeft] = useTaskField('items', index, 'question_left');
  const [shouldShowQLeftError, setShowQLeftError] = useState(false);
  const qLeftError = (!qLeft && 'Required.') || '';

  useLocalTaskError(qLeftError, () => setShowQLeftError(true));
  useEffect(() => {
    if (!qLeftError) setShowQLeftError(false);
  }, [qLeftError]);

  //for qRight error handling
  const [qRight] = useTaskField('items', index, 'question_right');
  const [shouldShowQRightError, setShowQRightError] = useState(false);
  const qRightError = (!qRight && 'Required.') || '';

  useLocalTaskError(qRightError, () => setShowQRightError(true));
  useEffect(() => {
    if (!qRightError) setShowQRightError(false);
  }, [qRightError]);

  const isExample = !!item?.is_example;

  return (
    <>
      <Typography
  variant='h6'
  sx={index !== 0 ? { marginTop: '40px', marginBottom: '24px' } : undefined}
>
  {index === 0 ? 'Example' : `Item ${index}`}
</Typography>

      {isExample && <RubricBlock index={index} />}

      {/* Two parts of question side by side */}
      <Grid container spacing={3}>
        <Grid item>
          <SimpleTaskField
            path={['items', index, 'question_left']}
            label='Question (left)'
            helperText={(shouldShowQLeftError && qLeftError) || 'Question text appears before the answer box'}
            error={shouldShowQLeftError && !!qLeftError}
            style={{ width: "314px" }}
            required
          />
        </Grid>

        <Grid item>
          <SimpleTaskField
            path={['items', index, 'question_right']}
            label='Question (right)'
            helperText={(shouldShowQRightError && qRightError) || 'Question text appears after the answer box'}
            error={shouldShowQRightError && !!qRightError}
            style={{ width: "314px" }}
            required
          />
        </Grid>
      </Grid>

      <Grid item xs={12} style={{ marginTop: 16 }}>
        <Typography sx={{ fontWeight: 400, marginBottom: 1, lineHeight: '150%',fontSize:'1rem' }}>Correct Answers*</Typography>
        <ChipList
          value={answers}
          onChange={setAnswers}
          error={!!answersError}
          helperText={answersError || `Answers that will be accepted as correct (max 1 for example).`}
          max={index === 0 ? 1 : undefined}
          maxLen={750}
        />
      </Grid>
    </>
  );
}

function ItemPhase2({ index }) {
  const classes = useStyles();
  const useField = partial(useTaskField, 'items', index);
  const [item, setItem] = useField();

  const [narrative] = useTaskField('items', index, 'stimulus_text');
  const [shouldShowNarrativeError, setShowNarrativeError] = useState(false);
  const narrativeValue = item?.stimulus_text || '';
  const words = countWords(narrativeValue);
  const narrativeError =
    ((!narrative || words < 40) && 'Narrative must be at least 40 words.') ||
    (words > 75 && 'Narrative must be at most 75 words.') ||
    '';

  useLocalTaskError(narrativeError, () => setShowNarrativeError(true));
  useEffect(() => {
    if (!narrativeError) setShowNarrativeError(false);
  }, [narrativeError]);

  const setDescription = (val) => setItem({ ...item, stimulus_text: val });

  // Decorative image only if required for phase 2 items
  const [decorativeImageId, setDecorativeImageId] = useTaskField(
    'items',
    index,
    'decorative_image'
  );

  // Calculate display number for item (1-based index)
  const displayNumber = index;

  return (
    <>
      <Typography variant="h6">{`Item ${displayNumber}`}</Typography>

         <Typography
  className={classes.narrativeLabel}
  sx={{ marginTop: '20px' }}
>
  Narrative new
</Typography>
      <Grid container spacing={2}>
        <Grid item xs={12}>
         <Typography
  sx={{
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: 'text.primary',
    marginBottom: '24px',
  }}
>
  The text should contain between 40 and 75 words.
</Typography>
          <SimpleTaskField
            path={['items', index, 'stimulus_text']}
            label="Narrative"
            value={narrativeValue}
            onChange={e => {
              setDescription(limitWordsAndSpaces(e.target.value, 75));
            }}
            multiline
            rows={9}
            style={{ width: '417px', height: '260px' }}
            helperText={
              (shouldShowNarrativeError && narrativeError) || `${words}/75 words`
            }
            error={shouldShowNarrativeError && !!narrativeError}
            required
          />
        </Grid>

        <Grid item xs={12} sm={6}>
         <Typography
  sx={{
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '1rem',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#000000 · 87%',
    marginBottom: 1,
  }}
>
  Decorative Image
</Typography>
          <ImagePicker
            label="Decorative image"
            value={decorativeImageId || ''}
            onChange={id => setDecorativeImageId(id)}
          />
            <Typography sx={{ fontSize: 13, color: '#757575', marginTop: '11px', }}>
            This appears at the bottom of the page. Image size: 110px x 110px.
          </Typography>
        </Grid>
      </Grid>

      {/* Rubric */}
      <RubricBlock index={index} />

      {/* Answers with radio options */}
      <Grid item xs={12} style={{ marginTop: 16 }}>
        <Typography sx={{ fontWeight: 400, marginBottom: 1,fontSize:'16px',lineHeight:'150%', letterSpacing:'0.15px',color:'black' }}>
          Answers
        </Typography>
        <TaskRadioOptionBlock path={['items', index, 'answers']} />
      </Grid>
    </>
  );
};

function ItemPhase3({ index }) {
    const classes = useStyles();
    const CHOICE_KEYWORDS_LIMIT = 750;
    const useField = partial(useTaskField, 'items', index);
    const [item, setItem] = useField();
    return (
        <>
         <Typography
  variant='h6'
  sx={{ marginTop: '0px', }}
>
  {`Item ${index}`}
</Typography>
            <RubricBlock index={index}/>

            {/* Headings above each image */}
            <Grid container spacing={2} style={{ marginBottom: 16 }}>
              {['A', 'B', 'C'].map((label, i) => (
                <Grid item xs={4} key={label}>
                 <Typography
  variant="subtitle1"
  sx={{
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 400,
    fontStyle: 'normal',
    fontSize: '16px',
    lineHeight: '150%',
    letterSpacing: '0.15px',
    color: '#000000 · 87%',
    textAlign: 'left',
    marginTop:'44px',
  }}
>
  {`Image ${label}`}
</Typography>
                  <ImagePicker
                    label={`Image ${label}`}
                    value={item?.answers?.[i]?.image || ''}
                    onChange={img => {
                      const answers = [...(item.answers || [])];
                      answers[i] = { ...answers[i], image: img };
                      setItem({ ...item, answers });
                    }}
                  />
                </Grid>
              ))}
            </Grid>
            <SimpleTaskField
                required
                label='Image A keyword(s)'
                path={['items', index, 'answers', 0, 'key']}
                helperText='Please type the keywords, delimited by commas.'
                limit={CHOICE_KEYWORDS_LIMIT}
                style={{ marginTop: '24px' }}
            />
            <SimpleTaskField
                required
                label='Image B keyword(s)'
                path={['items', index, 'answers', 1, 'key']}
                helperText='Please type the keywords, delimited by commas.'
                limit={CHOICE_KEYWORDS_LIMIT}
                style={{ marginTop: '24px' }}
            />
            <SimpleTaskField
                required
                label='Image C keyword(s)'
                path={['items', index, 'answers', 2, 'key']}
                helperText='Please type the keywords, delimited by commas.'
                limit={CHOICE_KEYWORDS_LIMIT}
                style={{ marginTop: '24px' }}
            />

       <Typography
  variant='h5'
  sx={{
    fontFamily: 'Roboto, Arial, sans-serif',
    fontWeight: 600,
    fontStyle: 'normal',
    fontSize: '20px',
    lineHeight: '100%',
    letterSpacing: '0',
    marginTop:'40px',
    marginBottom:'24px'
  }}
>
  {`Training Sentences`}
</Typography>

            <Grid item xs={12}>
                <SimpleTaskField
                    required
                    label='Image A Training Sentences'
                    path={['items', index, 'answers', 0, 'training_sentences']}
                    helperText='Please type at least 5 training sentences delimited by commas.'
                    multiline
                    rows={3}
                    style={{ marginTop:'24px' }}

                />
            </Grid>
            <Grid item xs={12}>
                <SimpleTaskField
                    required
                    label='Image B Training Sentences'
                    path={['items', index, 'answers', 1, 'training_sentences']}
                    helperText='Please type at least 5 training sentences delimited by commas.'
                    multiline
                    rows={3}
                    style={{ marginTop:'24px' }}

                />
            </Grid>
            <Grid item xs={12}>
                <SimpleTaskField
                    required
                    label='Image C Training Sentences'
                    path={['items', index, 'answers', 2, 'training_sentences']}
                    helperText='Please type at least 5 training sentences delimited by commas.'
                    multiline
                    rows={3}
                    style={{ marginTop:'24px' }}

                />
            </Grid>
        </>
    )
}
